/**
 * device/{userId}/{deviceId}/status 联调日志。
 * 开关：.env 中 REACT_APP_DEBUG_DEVICE_STATUS_MQTT=1，改后需重启 dev / 重新 build。
 * 统一 console.warn，避免生产 build 剔除 console.log。
 */

export function isDeviceStatusMqttDebugEnabled(): boolean {
  const v = process.env.REACT_APP_DEBUG_DEVICE_STATUS_MQTT?.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

let bootLogged = false;

function warn(message: string, detail?: Record<string, unknown>): void {
  if (!isDeviceStatusMqttDebugEnabled()) {
    return;
  }
  if (!bootLogged) {
    bootLogged = true;
    console.warn(
      '[WaveLink MQTT device status] 调试已开启（打印 topic + 原始 payload，console.warn）',
    );
  }
  if (detail !== undefined) {
    console.warn(message, detail);
  } else {
    console.warn(message);
  }
}

const lastLogAtByKey = new Map<string, number>();
const LOG_THROTTLE_MS = 1500;

function warnThrottled(key: string, message: string, detail: Record<string, unknown>): void {
  if (!isDeviceStatusMqttDebugEnabled()) {
    return;
  }
  if (!bootLogged) {
    bootLogged = true;
    console.warn(
      '[WaveLink MQTT device status] 调试已开启（打印 topic + 原始 payload，console.warn）',
    );
  }
  const now = Date.now();
  const last = lastLogAtByKey.get(key) ?? 0;
  if (now - last < LOG_THROTTLE_MS) {
    return;
  }
  lastLogAtByKey.set(key, now);
  console.warn(message, detail);
}

/** 是否为设备状态相关 MQTT 主题 */
export function isDeviceStatusMqttTopic(topic: string): boolean {
  const parts = topic.split('/');
  if (parts[0] !== 'device') {
    return false;
  }
  if (parts.length === 4 && parts[1] === 'status' && parts[2] === 'user') {
    return true;
  }
  if (parts.length === 4 && parts[3] === 'status') {
    return true;
  }
  return parts.includes('status');
}

function parseMessagePayload(message: string | Buffer): unknown {
  const text = message.toString();
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/** Broker → 客户端最早一层：MqttManager.on('message') */
export function logDeviceStatusMqttRawFromBroker(
  topic: string,
  message: string | Buffer,
): void {
  if (!isDeviceStatusMqttDebugEnabled() || !isDeviceStatusMqttTopic(topic)) {
    return;
  }
  warnThrottled(`broker:${topic}`, '[WaveLink MQTT device status] [Broker] 收到', {
    topic,
    data: parseMessagePayload(message),
  });
}

/** MqttAudioManager 路由前：scoped / user 级 status 主题 */
export function logDeviceStatusMqttRawInAudioManager(
  topic: string,
  message: string | Buffer,
  mqttUserId: string,
): void {
  if (!isDeviceStatusMqttDebugEnabled()) {
    return;
  }
  const parts = topic.split('/');
  const isScoped =
    parts[0] === 'device' && parts.length === 4 && parts[3] === 'status';
  const isUserLevel =
    parts[0] === 'device' && parts[1] === 'status' && parts[2] === 'user';

  if (!isScoped && !isUserLevel) {
    return;
  }

  const topicUserId = isScoped ? parts[1] : parts[3];
  const topicDeviceId = isScoped ? parts[2] : undefined;
  const data = parseMessagePayload(message);
  const rawType =
    data && typeof data === 'object' && data !== null && 'type' in data
      ? String((data as { type?: unknown }).type)
      : '(无 type)';

  warnThrottled(
    `am:${topic}`,
    '[WaveLink MQTT device status] [AudioManager] 收到',
    {
      topic,
      mqttUserId,
      topicUserId,
      topicDeviceId,
      userIdMatch: isScoped ? String(topicUserId) === String(mqttUserId) : undefined,
      rawType,
      data,
    },
  );
}

/** scoped 主题 userId 与登录用户不一致 */
export function logDeviceStatusScopedUserIdRejected(
  topic: string,
  topicUserId: string,
  topicDeviceId: string,
  mqttUserId: string,
): void {
  warnThrottled(
    `reject:${topic}`,
    '[WaveLink MQTT device status] scoped userId 不匹配，已忽略',
    {
      topic,
      topicUserId,
      topicDeviceId,
      mqttUserId,
    },
  );
}

/** 订阅 device/{userId}/{deviceId}/status 成功 */
export function logDeviceStatusSubscribed(topic: string): void {
  warn('[WaveLink MQTT device status] 已订阅', { topic });
}
